import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { Biohazard, Crosshair, Download, MapPinned, Milestone, Route, Shapes, ShieldAlert, Spline, Upload, Users } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { api, downloadSituation } from "../../api/client";
import type { SituationFormat, SituationLayer, SituationPhase } from "../../api/types";
import { designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { ChoiceCard, PanelGroup, PanelRow } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { SaveDossierHint } from "../../components/SaveDossierHint";
import { Select } from "../../components/Select";
import { Badge, Button, FloatingPanel, inputClass, Segmented } from "../../components/ui";
import { VIEW_META } from "../../components/viewMeta";
import { HelpButton } from "../../help/HelpButton";
import { k, t, tServer } from "../../i18n/i18n";
import { TRIAL_EXPORT_MESSAGE, useTrialExport } from "../../licence/trial";
import { effectiveOpo } from "../../map/units/useDeployment";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { OpenMissionDataButton } from "../openMissionData";
import { GraphicsImportDialog } from "./GraphicsImportDialog";
import { kmzIcons } from "./icons";
import { designateZoneWait } from "../../map/zones/zoneAttention";
import { outdatedRefusal, zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { usePendingZone, ZoneWaitCallout } from "../../map/zones/ZoneWaitCallout";
import { useWriteBlock } from "../../sharing/access";
import { Waiting } from "../../components/Waiting";

/** Exports de la situation (vue « Exports », registre C3 à C11) : la situation du dossier enregistré — unités
 *  par temps, symboles de mission, fuseau et lignes, graphiques, NRBC, ponts, points de passage obligés, réseau
 *  de manœuvre —
 *  vers les systèmes de l'OTAN et de simulation, comme l'écran d'export de l'iPad. */

const FORMATS: { key: SituationFormat; name: string; ext: string; targets: string; hint: string }[] = [
  {
    key: "cot",
    name: "ATAK (CoT)",
    ext: ".zip",
    targets: k("ATAK, WinTAK, iTAK"),
    hint: k("Paquet de données : un objet Cursor-on-Target par unité, tracé ou point, à importer d'un geste."),
  },
  {
    key: "kmz",
    name: "KMZ",
    ext: ".kmz",
    targets: k("Google Earth, QGIS, SitaWare"),
    hint: k("Symboles APP-6 de la carte et symboles de mission, un dossier par couche et par temps."),
  },
  {
    key: "nvg",
    name: "NVG 2.0",
    ext: ".nvg",
    targets: k("SitaWare, SICS, réseaux FMN"),
    hint: k("Calque de situation OTAN, unités codées en APP-6B, annotations en MIL-STD-2525C."),
  },
  {
    key: "msdl",
    name: "MSDL",
    ext: ".xml",
    targets: k("SWORD, SOULT"),
    hint: k("Initialisation d'une simulation : camps, unités, échelons et positions au temps choisi."),
  },
  {
    key: "sitrep",
    name: "SITREP",
    ext: ".txt",
    targets: k("Messagerie ADatP-3"),
    hint: k("Compte rendu de situation rédigé au format de message OTAN, à relire avant transmission."),
  },
];

const PHASES: { value: SituationPhase; label: string }[] = [
  { value: "T1", label: k("T1") },
  { value: "T2", label: k("T2") },
  { value: "T3", label: k("T3") },
  { value: "all", label: k("Tous") },
];

const LAYER_ICONS: Record<SituationLayer, ReactNode> = {
  units: <Users size={14} />,
  missions: <Crosshair size={14} />,
  corridor: <Milestone size={14} />,
  graphics: <Shapes size={14} />,
  fire_zones: <Crosshair size={14} />,
  annotations: <MapPinned size={14} />,
  cbrn: <Biohazard size={14} />,
  bridges: <Spline size={14} />,
  chokepoints: <ShieldAlert size={14} />,
  roads: <Route size={14} />,
};

/** Couches cochées à l'ouverture : toutes, sauf les points de passage obligés (données du camp, par centaines). */
const DEFAULT_LAYERS: SituationLayer[] = ["units", "missions", "corridor", "graphics", "fire_zones", "annotations", "cbrn", "bridges", "roads"];

const CLASSIFICATION_LABELS: Record<string, string> = {
  "NON PROTEGE": k("Non protégé"),
  "DIFFUSION RESTREINTE": k("Diffusion restreinte"),
  "NATO UNCLASSIFIED": k("NATO Unclassified"),
};

/** Libellé d'une classification, dans la langue de l'interface (valeur inconnue : telle quelle). */
function classificationLabel(value: string): string {
  const label = CLASSIFICATION_LABELS[value];
  return label ? t(label) : value;
}

/** Choix du format : cartes de choix en groupe radio (une seule tabulable, flèches pour changer). */
function FormatList({ value, onChange }: { value: SituationFormat; onChange: (fmt: SituationFormat) => void }) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : e.key === "ArrowUp" || e.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const index = (FORMATS.findIndex((f) => f.key === value) + step + FORMATS.length) % FORMATS.length;
    onChange(FORMATS[index]!.key);
    e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')[index]?.focus();
  };
  return (
    <div className="space-y-1" role="radiogroup" aria-label={t("Format")} onKeyDown={onKeyDown}>
      {FORMATS.map((f) => (
        <ChoiceCard
          key={f.key}
          radio
          selected={f.key === value}
          tabIndex={f.key === value ? 0 : -1}
          onClick={() => onChange(f.key)}
          title={f.name}
          detail={`${t(f.targets)} · ${f.ext}`}
        />
      ))}
    </div>
  );
}

/** Couche à exporter : case (toute la ligne la bascule), icône, nom, compteur à droite. */
function ExportLayerRow({
  label,
  icon,
  count,
  checked,
  onChange,
}: {
  label: string;
  icon: ReactNode;
  count: number | null;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const empty = count === 0;
  return (
    <PanelRow
      title={
        <Checkbox
          className="w-full"
          checked={checked && !empty}
          disabled={empty}
          onChange={onChange}
          title={empty ? t("Aucun objet de cette couche au temps choisi") : undefined}
          label={
            <span className="flex items-center gap-2 font-normal">
              <span className="flex shrink-0 text-ink-muted">{icon}</span>
              {label}
            </span>
          }
        />
      }
      state={<Badge>{count === null ? "…" : count}</Badge>}
    />
  );
}

/** État du bouton « Exporter » (R12) : désactivé seulement pendant une requête (export en cours, premier compte des
 *  objets) ; bloqué — cliquable, il désigne l'action attendue — faute d'objet coché ou tant que la zone libre du
 *  dossier n'est pas prête (le compte des objets est alors refusé : il ne désactive rien). */
export function exportButtonState({
  running,
  summaryPending,
  pendingZone,
  nothingChosen,
}: {
  running: boolean;
  summaryPending: boolean;
  pendingZone: unknown;
  nothingChosen: boolean;
}): { disabled: boolean; blocked: boolean } {
  return { disabled: running || (summaryPending && !pendingZone), blocked: nothingChosen || Boolean(pendingZone) };
}

export function ExportsPanel() {
  // Importer des graphiques écrit dans le dossier : bloqué en lecture seule (docs/17 §3.2), l'appui désigne le bandeau.
  const writeBlock = useWriteBlock();
  // Licence d'essai (registre F44) : « Exporter » grisé (vue ouverte d'avant ou par un autre chemin) ; l'import reste.
  const trialExport = useTrialExport();
  const exerciseId = useUi((s) => s.exerciseId);
  const params = useUi((s) => s.deploymentParams);
  const mapPhase = useUi((s) => s.phase);
  const notify = useUi((s) => s.notify);
  const opo = effectiveOpo(params, useUi((s) => s.opo));
  const { data: exercises } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises?.find((e) => e.id === exerciseId);
  const complete = Boolean(exercise?.map_slug && exercise.parameters.level);
  const centac = exercise?.parameters.level === "CENTAC";
  // Brouillon des Données initiales différent du dossier enregistré : l'export porte sur le dossier enregistré.
  const unsaved = Boolean(exerciseId && params && params.exercise_id !== exerciseId);

  const [format, setFormat] = useState<SituationFormat>("kmz");
  const [importing, setImporting] = useState(false);
  const [phase, setPhase] = useState<SituationPhase>(mapPhase);
  const [layers, setLayers] = useState<SituationLayer[]>(DEFAULT_LAYERS);
  const [classification, setClassification] = useState("NON PROTEGE");
  // Un seul temps : le MSDL initialise une simulation (une position par unité), le SITREP rend compte d'une
  // situation à une heure donnée (décision du 23/10/2026).
  const singlePhase = format === "msdl" || format === "sitrep";
  const shownPhase: SituationPhase = singlePhase && phase === "all" ? "T1" : phase;

  const summary = useQuery({
    queryKey: ["situation-exports", exerciseId, shownPhase, opo],
    queryFn: () => api.situationSummary(exerciseId as string, shownPhase, opo),
    enabled: Boolean(exerciseId && complete),
    placeholderData: keepPreviousData, // changement de temps : les compteurs restent affichés
    staleTime: 0,
    retry: false,
  });
  const counts = new Map(summary.data?.layers.map((l) => [l.key, l.count]));
  const chosen = layers.filter((key) => (counts.get(key) ?? 0) > 0);
  const total =
    format === "sitrep"
      ? null
      : format === "msdl"
        ? (counts.get("units") ?? 0)
        : chosen.reduce((sum, key) => sum + (counts.get(key) ?? 0), 0);

  const run = useMutation({
    mutationFn: async () => {
      const icons = format === "kmz" ? await kmzIcons(summary.data?.icons ?? [], chosen) : undefined;
      return downloadSituation(
        exerciseId as string,
        format,
        { phase: shownPhase, layers: chosen, classification, icons },
        opo,
      );
    },
    onSuccess: (name) => notify({ text: t("Export enregistré"), detail: name, tone: "ok" }),
  });

  const info = FORMATS.find((f) => f.key === format)!;
  const choose = (next: SituationFormat) => {
    setFormat(next);
    run.reset();
  };
  const choosePhase = (next: SituationPhase) => {
    setPhase(next);
    run.reset();
  };
  const toggle = (key: SituationLayer, on: boolean) =>
    setLayers((current) => (on ? [...current.filter((k) => k !== key), key] : current.filter((k) => k !== key)));

  const layersRef = useRef<HTMLDivElement>(null);
  // Aucune couche cochée qui contienne des objets : « Exporter » désigne la liste des couches (R12).
  const nothingChosen = Boolean(summary.data) && total === 0;
  const designateLayers = () => {
    designate(layersRef.current, "ring");
    notify({ text: t("Cochez au moins une couche qui contient des objets."), tone: "info" });
  };
  const ready = Boolean(exerciseId && complete);
  // Zone libre pas encore prête : rien à exporter (409 du serveur) ; « Exporter » désigne l'action attendue.
  const pendingZone = usePendingZone();
  const onExportBlocked = () => (pendingZone ? designateZoneWait(pendingZone, "export") : designateLayers());
  const zoneRefused = (error: unknown) => zoneRefusalOf(error) !== null;
  // Refus « terrain pas prêt » d'avant que la zone soit prête : sans objet, plus affiché.
  const runError = run.isError && !outdatedRefusal(run.error, pendingZone) ? run.error : null;

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={VIEW_META.exports.icon}
      title={t("Exports")}
      actions={<HelpButton screen="exports" buttonSize="sm" size={16} />}
      footer={
        ready ? (
          // Action du volet, hors de la zone qui défile : « Exporter » reste visible.
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Button
              variant="primary"
              className="w-full"
              onClick={() => run.mutate()}
              {...exportButtonState({ running: run.isPending, summaryPending: summary.isPending, pendingZone, nothingChosen })}
              onBlocked={onExportBlocked}
              {...(trialExport.blocked ? trialExport : {})}
            >
              {run.isPending ? <Spinner decorative /> : <Download size={14} />}
              {run.isPending ? <Waiting>{t("Préparation du fichier…")}</Waiting> : t("Exporter en {format}", { format: info.name })}
            </Button>
            <p className="text-center text-meta text-ink-muted tabular-nums">
              {trialExport.blocked
                ? t(TRIAL_EXPORT_MESSAGE)
                : pendingZone
                ? t("Disponible quand le terrain sera prêt.")
                : total === null
                ? t("Message texte {ext}", { ext: info.ext })
                : total === 0 && summary.data
                  ? t("Cochez au moins une couche qui contient des objets.")
                  : t("{objects} · fichier {ext}", { objects: plural(total, t("objet"), t("objets")), ext: info.ext })}
            </p>
            <Presence show={runError !== null}>
              {runError &&
                (zoneRefused(runError) ? <ZoneWaitCallout message={runError.message} error={runError} /> : <Callout tone="danger">{runError.message}</Callout>)}
            </Presence>
          </div>
        ) : undefined
      }
    >
      {!ready ? (
        <Motion origin="fade">
          <EmptyState variant="inline" action={<OpenMissionDataButton />}>
            <p className="text-ink-soft">
              {t(
                "Les exports reprennent la situation d'un dossier enregistré : unités à chaque temps, symboles de mission, mesures de coordination, graphiques, NRBC et terrain.",
              )}
            </p>
            <p className="mt-2">
              {!exerciseId ? t("Pour exporter la situation, créez d'abord le dossier.") : t("Choisissez d'abord un camp et un niveau.")}
            </p>
          </EmptyState>
        </Motion>
      ) : (
        <Motion origin="fade">
          {/* Brouillon des Données initiales non enregistré : l'export porte sur le dossier enregistré. */}
          <Presence show={unsaved}>
            <Motion origin="fade">
              <SaveDossierHint action="inclure vos dernières modifications" variant="banner" />
            </Motion>
          </Presence>

          <PanelGroup title={t("Format")} flush>
            <div className="px-4 pb-1">
              <FormatList value={format} onChange={choose} />
              <Motion key={format} origin="fade" className="mt-2 text-meta leading-relaxed text-ink-soft">
                {t(info.hint)}
              </Motion>
            </div>
          </PanelGroup>

          <PanelGroup title={format === "msdl" ? t("Temps (positions de départ de la simulation)") : t("Temps")} flush>
            <div className="px-4 pb-1">
              <Segmented
                ariaLabel={format === "msdl" ? t("Temps (positions de départ de la simulation)") : t("Temps")}
                options={(singlePhase ? PHASES.slice(0, 3) : PHASES).map((p) => ({ ...p, label: t(p.label) }))}
                value={shownPhase}
                onChange={choosePhase}
              />
              {centac && (
                <p className="mt-2 text-meta leading-snug text-ink-muted">
                  {t("Rotation CENTAC : situation de l'OPO {opo}, celui affiché sur la carte.", { opo })}
                </p>
              )}
            </div>
          </PanelGroup>

          {pendingZone || summary.isError ? (
            <div className="border-b border-line px-4 py-3">
              {pendingZone || zoneRefused(summary.error) ? (
                <ZoneWaitCallout
                  message={summary.isError && zoneRefused(summary.error) ? summary.error.message : undefined}
                  error={summary.error}
                />
              ) : (
                <Callout tone="danger">{summary.error?.message}</Callout>
              )}
            </div>
          ) : format === "sitrep" ? (
            <PanelGroup title={t("Classification")} flush>
              <Motion origin="fade" className="space-y-2.5 px-4 pb-1">
                <Select
                  className={inputClass}
                  value={classification}
                  onChange={(e) => setClassification(e.target.value)}
                  aria-label={t("Classification")}
                >
                  {(summary.data?.classifications ?? Object.keys(CLASSIFICATION_LABELS)).map((c) => (
                    <option key={c} value={c}>
                      {classificationLabel(c)}
                    </option>
                  ))}
                </Select>
                <p className="text-meta leading-relaxed text-ink-muted">
                  {t(
                    "Le SITREP rend compte de toute la situation au temps choisi : ennemi, amis, mission, lignes de coordination, terrain et obstacles, NRBC.",
                  )}
                </p>
              </Motion>
            </PanelGroup>
          ) : format === "msdl" ? (
            <PanelGroup title={t("Contenu")}>
              <PanelRow
                icon={LAYER_ICONS.units}
                title={t("Unités")}
                state={<Badge>{summary.data ? (counts.get("units") ?? 0) : "…"}</Badge>}
              />
              <p className="px-2 pt-1 pb-1 text-meta leading-relaxed text-ink-muted">
                {t("Le MSDL décrit les forces en présence, unités non identifiées des annotations comprises ; les mesures de coordination, les graphiques et les autres annotations passent par NVG, KMZ ou ATAK.")}
              </p>
            </PanelGroup>
          ) : (
            <PanelGroup
              title={t("Couches")}
              actions={
                <Button
                  variant="link"
                  onClick={() => setLayers(chosen.length ? [] : (summary.data?.layers.map((l) => l.key) ?? DEFAULT_LAYERS))}
                >
                  {chosen.length ? t("Tout décocher") : t("Tout cocher")}
                </Button>
              }
            >
              <div ref={layersRef} className="space-y-px rounded-lg">
                {(summary.data?.layers ?? []).map((layer) => (
                  <ExportLayerRow
                    key={layer.key}
                    label={tServer(layer.label)}
                    icon={LAYER_ICONS[layer.key]}
                    count={layer.count}
                    checked={layers.includes(layer.key)}
                    onChange={(on) => toggle(layer.key, on)}
                  />
                ))}
                {!summary.data && (
                  <p className="flex min-h-8 items-center gap-2 px-2 text-ui text-ink-muted">
                    <Spinner decorative /> {<Waiting>{t("Inventaire de la situation…")}</Waiting>}
                  </p>
                )}
              </div>
            </PanelGroup>
          )}

          {/* Import : avant la barre « Exporter », toujours la dernière. */}
          <PanelGroup title={t("Importer")} flush>
            <div className="space-y-2 px-4 pb-1">
              <p className="text-meta leading-relaxed text-ink-muted">
                {t(
                  "Zones, lignes et panaches NRBC d'un fichier ATAK, KMZ ou NVG, ajoutés aux graphiques du dossier après contrôle ; les unités viennent de l'ordre de bataille du dossier.",
                )}
              </p>
              <Button variant="secondary" className="w-full" onClick={() => setImporting(true)} {...writeBlock}>
                <Upload size={14} /> {t("Importer des graphiques")}
              </Button>
            </div>
          </PanelGroup>
        </Motion>
      )}
      {/* Au niveau de la page : le panneau animé retiendrait la fenêtre dans son cadre. */}
      {createPortal(
        <Presence show={importing && Boolean(exerciseId)}>
          {importing && exerciseId && (
            <GraphicsImportDialog exerciseId={exerciseId} onClose={() => setImporting(false)} />
          )}
        </Presence>,
        document.body,
      )}
    </FloatingPanel>
  );
}
